import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { authFetch } from "../api";
import Header from "../components/Header";

/**
 * AlertsPage — what changed on my saved searches, grouped by search.
 *
 * This is where the "View all changes" button in a digest email lands. It deliberately is NOT
 * the Kanban board: the board shows what matches the filter RIGHT NOW, while an alert can be
 * about a listing that has since left the search (repriced out of range, went into contract).
 * Those listings can never appear on the board, and they are often the most interesting ones.
 *
 * It reads `notifications` payloads the detector already wrote — the same rows the email is
 * built from — so the page and the email cannot disagree.
 */

const WINDOWS = [7, 30, 90];

const TONE_COLOR = { good: "#2e7d32", bad: "#c62828", neutral: "#555" };
const CADENCE_LABEL = { daily: "daily", twice_weekly: "twice a week", weekly: "weekly" };

const money = (v) =>
  v === null || v === undefined || Number.isNaN(Number(v))
    ? "N/A"
    : `$${Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

// A change can be weeks old on this page (the window goes to 90 days) while the card's meta
// line shows the listing's CURRENT status — without a date, "Withdrawn from market" next to
// "ACTIVE" reads as a contradiction rather than as history.
function changeDate(change) {
  if (!change.at) return null;
  const d = new Date(change.at);
  return Number.isNaN(d.getTime())
    ? null
    : d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function ChangeLine({ change }) {
  const when = changeDate(change);
  const stamp = when && (
    <span style={{ color: "#999", fontWeight: 400, marginLeft: "6px" }}>{when}</span>
  );

  if (change.kind === "price") {
    const up = (change.new || 0) > (change.old || 0);
    const color = up ? TONE_COLOR.bad : TONE_COLOR.good; // a cut is good news for a buyer
    return (
      <div style={{ fontSize: "13px", color, marginTop: "2px" }}>
        {money(change.old)} → <strong>{money(change.new)}</strong>
        {change.pct !== null && change.pct !== undefined && (
          <span> ({change.pct > 0 ? "+" : ""}{Number(change.pct).toFixed(1)}%)</span>
        )}
        {stamp}
      </div>
    );
  }
  if (change.kind === "status") {
    return (
      <div style={{ fontSize: "13px", color: TONE_COLOR[change.tone] || TONE_COLOR.neutral, marginTop: "2px", fontWeight: 600 }}>
        {change.label}{stamp}
      </div>
    );
  }
  return (
    <div style={{ fontSize: "13px", color: "#1565c0", marginTop: "2px" }}>
      New to this search{stamp}
    </div>
  );
}

function ListingCard({ listing }) {
  const meta = [
    listing.price != null ? money(listing.price) : null,
    listing.bedrooms != null ? (Number(listing.bedrooms) === 0 ? "Studio" : `${Number(listing.bedrooms)} bd`) : null,
    listing.bathrooms != null ? `${Number(listing.bathrooms)} ba` : null,
    // Prod carries junk like total_sqft = 1; below 100 sf is never a real apartment.
    listing.sqft && Number(listing.sqft) >= 100 ? `${Number(listing.sqft).toLocaleString()} sf` : null,
    listing.cap_rate != null ? `cap ${Number(listing.cap_rate).toFixed(2)}%` : null,
    listing.listing_status || null,
  ].filter(Boolean).join(" • ");

  return (
    <div style={{ display: "flex", gap: "12px", padding: "12px", border: "1px solid #dee2e6", borderRadius: "6px", background: "#fff", marginBottom: "10px" }}>
      {listing.photo_url && (
        <a href={`/predict/${listing.listing_id}`} style={{ flexShrink: 0 }}>
          <img src={listing.photo_url} alt="" width="120" height="84"
            style={{ width: "120px", height: "84px", objectFit: "cover", borderRadius: "4px", background: "#eceff1", display: "block" }} />
        </a>
      )}
      <div style={{ minWidth: 0 }}>
        <a href={`/predict/${listing.listing_id}`}
          style={{ color: "#007bff", textDecoration: "none", fontSize: "15px", fontWeight: 700 }}>
          {(listing.address || "N/A").trim()}{listing.unit_number ? ` #${listing.unit_number}` : ""}
        </a>
        <div style={{ fontSize: "12px", color: "#666", margin: "3px 0 4px" }}>{meta}</div>
        {(listing.changes || []).map((change, i) => <ChangeLine key={i} change={change} />)}
      </div>
    </div>
  );
}

export default function AlertsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);

  // A digest link arrives as /alerts?search=<id>; keep that scoping visible in the UI so the
  // reader knows they are looking at one search and can widen to all of them.
  const searchId = searchParams.get("search");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ days: String(days) });
      if (searchId) params.set("search_id", searchId);
      const res = await authFetch(`/saved-searches/alerts?${params.toString()}`);
      setData(res && !res.detail ? res : { searches: [] });
    } catch (e) {
      console.error("loading alerts failed:", e);
      setData({ searches: [] });
    } finally {
      setLoading(false);
    }
  }, [days, searchId]);

  useEffect(() => { load(); }, [load]);

  const total = useMemo(
    () => (data?.searches || []).reduce((n, s) => n + s.listings.length, 0),
    [data]
  );

  const pageStyle = { minHeight: "100vh", backgroundColor: "#f5f5f5" };
  const containerStyle = { padding: "20px", maxWidth: "1000px", margin: "0 auto" };

  return (
    <div style={pageStyle}>
      <Header showLogout={true} />
      <div style={containerStyle}>
        <div style={{ display: "flex", alignItems: "baseline", gap: "12px", flexWrap: "wrap", marginBottom: "6px" }}>
          <h1 style={{ margin: 0, fontSize: "24px" }}>Saved search alerts</h1>
          <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: "8px" }}>
            {searchId && (
              <button onClick={() => setSearchParams({})}
                style={{ padding: "6px 12px", fontSize: "13px", border: "1px solid #ced4da", borderRadius: "4px", background: "#fff", cursor: "pointer" }}>
                Show all searches
              </button>
            )}
            <select value={days} onChange={(e) => setDays(Number(e.target.value))}
              style={{ padding: "6px 8px", fontSize: "13px", border: "1px solid #ced4da", borderRadius: "4px", background: "#fff" }}>
              {WINDOWS.map((d) => <option key={d} value={d}>Last {d} days</option>)}
            </select>
          </div>
        </div>
        <p style={{ fontSize: "13px", color: "#666", marginTop: 0 }}>
          Changes the alert detector found. Includes listings that have since left a search —
          those can't appear on the pipeline board.
        </p>

        {loading && <div style={{ textAlign: "center", marginTop: "40px" }}>Loading…</div>}

        {!loading && (data?.searches || []).length === 0 && (
          <div style={{ background: "#fff", border: "1px solid #dee2e6", borderRadius: "8px", padding: "30px", textAlign: "center", color: "#666" }}>
            You have no saved searches yet. Save one from the pipeline board and tick
            "Email me when this search changes".
          </div>
        )}

        {!loading && (data?.searches || []).length > 0 && total === 0 && (
          <div style={{ background: "#fff", border: "1px solid #dee2e6", borderRadius: "8px", padding: "24px", color: "#666" }}>
            Nothing has changed on your saved searches in the last {days} days.
          </div>
        )}

        {!loading && (data?.searches || []).map(({ saved_search: search, listings }) => (
          <div key={search.id} style={{ marginBottom: "30px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "10px", borderBottom: "2px solid #000", paddingBottom: "6px", marginBottom: "12px" }}>
              <span style={{ fontSize: "17px", fontWeight: 700 }}>{search.name}</span>
              <span style={{ fontSize: "12px", color: "#666" }}>
                {listings.length} listing{listings.length === 1 ? "" : "s"}
              </span>
              {search.notify_enabled ? (
                <span style={{ fontSize: "11px", fontWeight: 600, color: "#1565c0", background: "#e3f2fd", border: "1px solid #90caf9", borderRadius: "10px", padding: "2px 8px" }}>
                  🔔 {CADENCE_LABEL[search.notify_frequency] || "daily"}
                </span>
              ) : (
                <span title="Alerts are off for this search — you can still see changes here"
                  style={{ fontSize: "11px", fontWeight: 600, color: "#666", background: "#f1f3f5", border: "1px solid #dee2e6", borderRadius: "10px", padding: "2px 8px" }}>
                  alerts off
                </span>
              )}
              <a href="/pipeline" style={{ marginLeft: "auto", fontSize: "12px", color: "#007bff", textDecoration: "none" }}>
                Open the board →
              </a>
            </div>
            {listings.length === 0 ? (
              <div style={{ fontSize: "13px", color: "#888" }}>No changes in this window.</div>
            ) : (
              listings.map((listing) => <ListingCard key={listing.listing_id} listing={listing} />)
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
